import { Route } from "vue-router";
import { User, UserInfo } from "@/types";
import {
  VuexModule,
  Module,
  Mutation,
  Action,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import { login } from "@/utils/demo-api";
import {
  privilegeFromProfesi,
  privilegePrefixFromProfesi
} from "@/constants/privilegeProfesiDefault";
import { parseActingUnitParam } from "@/utils/actingUnit";
import { UnitGate } from "@/utility/PemeriksaanReadonly";
import {
  getUser,
  getToken,
  setToken,
  setUser,
  cleanSession
} from "@/utils/app-util";
import ListPasienRalan from "./new/ListPasienRalan";
import { storeDokter } from "./dokter";
import storeListPasienRalan from "@/store/modules/new/ListPasienRalan";
import UserPreference, {
  UserPreference as UserPreferenceContent
} from "@/services/UserPreference";
import UserSvc from "@/services/User";
import EncounterIndex from "./EncounterIndex";
import {
  canAccessForm as canAccessFormUtil,
  accessibleForms as accessibleFormsUtil,
  FormAction
} from "@/utils/form-access";

export interface UserState {
  callingAPI: boolean;
  searching: string;
  user: User;
  token: string;
  // mode: string;
  userInfo: UserInfo;
  signedIn: boolean;
}

/** Unit segmen yang sedang dibuka dari Riwayat Pemeriksaan. */
export interface UnitSegmentSelection {
  unitId: string;
  unitNama: string;
  unitGroupId: string | null;
  zona: string | null;
  register: string;
}

/** Unit FISIK tempat pengguna login — selalu yang tersimpan di sessionStorage. */
export interface LoginUnitSnapshot {
  unit_id: string;
  unit: string;
  unit_group_id: string;
  zona: string;
}

let preferenceSaveQueue: Promise<unknown> = Promise.resolve();

@Module({ store, dynamic: true, name: "userModule" })
class UserModule extends VuexModule implements UserState {
  public callingAPI = false;
  public searching = "";
  user = getUser();
  token = getToken();
  profile = null;
  session: {
    is_password_expired: boolean;
    is_password_reseted: boolean;
    message: string;
  } = null;

  public userInfo = {} as UserInfo;
  public signedIn = false;

  public preference: UserPreferenceContent | null = null;

  unitSegment: UnitSegmentSelection | null = null;
  loginUnit: LoginUnitSnapshot | null = null;
  actingApplied = false;

  get isSignedIn(): boolean {
    // return this.user.email && this.token ? true : false;
    return this.user.id && this.token ? true : false;
  }

  get isMedis(): boolean {
    return this.user.profesi === "Dokter" || this.user.profesi === "Psikolog";
  }

  get isSuperAdmin(): boolean {
    return this.user?.role?.id === 1;
  }

  get isDokter(): boolean {
    return this.user?.profesi === "Dokter";
  }

  get loginUnitId(): string {
    return this.loginUnit ? this.loginUnit.unit_id : this.user?.unit_id ?? "";
  }

  get loginUnitNama(): string {
    return this.loginUnit ? this.loginUnit.unit : this.user?.unit ?? "";
  }

  get segmentUnitId(): string | null {
    return this.unitSegment ? this.unitSegment.unitId : null;
  }

  get segmentUnitNama(): string {
    if (!this.unitSegment) return "";
    return this.unitSegment.unitNama || this.unitSegment.unitId;
  }

  get isActingAsOtherUnit(): boolean {
    return this.actingApplied;
  }

  get unitGate(): UnitGate | null {
    if (!this.unitSegment) return null;

    return {
      segmentUnitId: this.unitSegment.unitId,
      segmentUnitNama: this.unitSegment.unitNama || this.unitSegment.unitId,
      loginUnitId: this.loginUnitId,
      loginUnitNama: this.loginUnitNama,
      bolehLintasUnit: this.canActAcrossUnit
    };
  }

  get canActAcrossUnit(): boolean {
    return (
      this.isSuperAdmin ||
      this.isDokter ||
      this.hasAssignedPrivilege("unit_bertindak", "lintas_unit")
    );
  }

  get canAccessRefAskep(): boolean {
    return this.hasPrivilege("master_ref_askep", "view");
  }

  get canVerifyAsesmenKeperawatan(): boolean {
    return this.hasPrivilege("asesmen_keperawatan", "verify");
  }

  get hasPrivilege() {
    return (domain: string, action: string): boolean => {
      if (this.isSuperAdmin) return true;
      const privs = this.user?.privileges;
      if (privs && privs[domain] && privs[domain][action] === true) return true;
      return privilegeFromProfesi(domain, action, this.user?.profesi);
    };
  }

  get hasAssignedPrivilege() {
    return (domain: string, action: string): boolean => {
      const privs = this.user?.privileges;
      return !!(privs && privs[domain] && privs[domain][action] === true);
    };
  }

  get hasPrivilegePrefix() {
    return (prefix: string, action: string): boolean => {
      if (this.isSuperAdmin) return true;
      const privs = this.user?.privileges;
      if (
        privs &&
        Object.keys(privs).some(
          d => d.indexOf(prefix) === 0 && privs[d][action] === true
        )
      ) {
        return true;
      }
      return privilegePrefixFromProfesi(prefix, action, this.user?.profesi);
    };
  }

  get canApproveRevisionAccess(): boolean {
    if (this.isSuperAdmin) return true;
    return !this.isDokter && this.hasPrivilege("revision_access", "approve");
  }

  get canAccessRevisionInbox(): boolean {
    return this.canApproveRevisionAccess;
  }

  get canAccessForm() {
    return (code: string, action: FormAction = "view"): boolean =>
      canAccessFormUtil(
        this.user?.unit_id,
        this.user?.profesi,
        this.user?.role?.id,
        code,
        action,
        this.extraKategori
      );
  }

  get extraKategori(): string[] {
    const scope = this.user?.unit_scope;
    return scope && scope.is_kepala_ruang ? ["kepala_ruang"] : [];
  }

  get scopedUnitId(): string | null {
    const scope = this.user?.unit_scope;
    if (scope && scope.is_kepala_ruang && scope.unit_id) return scope.unit_id;
    return this.user?.unit_id || null;
  }

  get isUnitScopeLocked(): boolean {
    const scope = this.user?.unit_scope;
    return !!(scope && scope.is_kepala_ruang && scope.unit_id);
  }

  get accessibleForms(): string[] {
    return accessibleFormsUtil(
      this.user?.unit_id,
      this.user?.profesi,
      this.user?.role?.id,
      "view",
      this.extraKategori
    );
  }

  @Mutation
  private loginLoading() {
    this.callingAPI = !this.callingAPI;
  }

  @Mutation
  private globalSearching() {
    this.searching = this.searching === "" ? "loading" : "";
  }

  @Mutation
  private SET_USER(user: User): void {
    this.user = user;
  }

  @Mutation
  private SET_UNIT_SEGMENT(seg: UnitSegmentSelection | null): void {
    this.unitSegment = seg;
  }

  @Mutation
  private SET_LOGIN_UNIT(snap: LoginUnitSnapshot | null): void {
    this.loginUnit = snap;
  }

  @Mutation
  private APPLY_SEGMENT_TO_USER(): void {
    if (!this.unitSegment || !this.user) return;

    this.user.unit_id = this.unitSegment.unitId;
    this.user.unit = this.unitSegment.unitNama;
    if (this.unitSegment.unitGroupId !== null) {
      this.user.unit_group_id = this.unitSegment.unitGroupId;
    }
    if (this.unitSegment.zona !== null) {
      this.user.zona = this.unitSegment.zona;
    }
    this.actingApplied = true;
  }

  @Mutation
  private RESTORE_LOGIN_UNIT(): void {
    if (!this.loginUnit || !this.user) {
      this.actingApplied = false;
      return;
    }

    this.user.unit_id = this.loginUnit.unit_id;
    this.user.unit = this.loginUnit.unit;
    this.user.unit_group_id = this.loginUnit.unit_group_id;
    this.user.zona = this.loginUnit.zona;
    this.actingApplied = false;
  }

  @Mutation
  private PATCH_SEGMENT_META(meta: {
    unitId: string;
    unitNama: string;
    unitGroupId: string | null;
    zona: string | null;
  }): void {
    if (!this.unitSegment || this.unitSegment.unitId !== meta.unitId) return;

    this.unitSegment.unitNama = meta.unitNama || this.unitSegment.unitId;
    this.unitSegment.unitGroupId = meta.unitGroupId;
    this.unitSegment.zona = meta.zona;

    if (this.actingApplied && this.user) {
      this.user.unit = this.unitSegment.unitNama;
      if (meta.unitGroupId !== null) this.user.unit_group_id = meta.unitGroupId;
      if (meta.zona !== null) this.user.zona = meta.zona;
    }
  }

  @Mutation
  private CLEAR_ACTING_STATE(): void {
    this.unitSegment = null;
    this.loginUnit = null;
    this.actingApplied = false;
  }

  @Mutation
  private SET_TOKEN(_token: string) {
    this.token = _token;
  }

  @Mutation
  private setUserInfo(_userInfo: UserInfo) {
    this.userInfo = _userInfo;
  }

  @Mutation
  private SET_PREFERENCE(pref: UserPreferenceContent | null) {
    this.preference = pref;
  }

  @Mutation
  private MERGE_PREFERENCE(pref: UserPreferenceContent) {
    this.preference = { ...(this.preference || {}), ...pref };
  }

  @Mutation
  private SET_PROFILE(data: any) {
    this.profile = data;
  }

  @Mutation
  private SET_SESSION(data: any) {
    this.session = data;
  }

  @Action({ rawError: true })
  async logout() {
    if (this.token) {
      const logout = await UserSvc.logout(this.user.id);

      if (logout?.status) {
        this.clearSession();
      } else {
        alert("unsucsessful logout!");
      }

      return logout;
    } else {
      this.clearSession();
    }
  }

  @Action({ rawError: true })
  async clearSession() {
    this.CLEAR_ACTING_STATE();
    this.SET_TOKEN("");
    this.SET_USER({} as User);
    this.SET_PROFILE(null);
    this.SET_SESSION(null);
    this.SET_PREFERENCE(null);

    storeDokter.CLEAR_ALL_LISTS();
    storeListPasienRalan.clearAll();
    EncounterIndex.RESET_ALL();

    cleanSession();
  }

  @Action({ rawError: true })
  async setLogin(data: { token: string; user: User }) {
    await setToken(data.token);
    await setUser(JSON.stringify(data.user));

    await this.SET_TOKEN(data.token);
    await this.SET_USER(data.user);
  }

  @Action({ rawError: true })
  public async signIn(userInfo: {
    username: string;
    password: string;
    unit: string;
    unit_id: string;
    unit_group_id: string;
    zona: string;
  }) {
    const { username, password, unit, unit_id, unit_group_id, zona } = userInfo;
    const { data } = await login("login", {
      username,
      password,
      unit,
      unit_id,
      unit_group_id,
      zona
    });

    Object.assign(data.user, {
      isMedis:
        data.user.profesi === "Dokter" || data.user.profesi === "Psikolog"
    });
    setToken(data.access_token);
    setUser(JSON.stringify(data.user));

    this.SET_USER(data.user);
    this.SET_TOKEN(data.access_token);
    this.SET_SESSION({
      message: data.message,
      is_password_expired: data.user?.is_password_expired,
      is_password_reseted: data.user?.is_password_reseted
    });

    if (data.user.isMedis && [1, 268].includes(data.user.id)) {
      EncounterIndex.getPasienRanapDilayani();
    }
  }

  @Action({ rawError: true })
  public async getPreference() {
    const userId = this.user.id;
    const data = await UserPreference.find(userId);

    if (this.user.id === userId) {
      this.SET_PREFERENCE(data);
    }
  }

  @Action({ rawError: true })
  public async savePreference(pref: UserPreferenceContent) {
    const userId = this.user.id;
    const request = preferenceSaveQueue
      .catch(() => undefined)
      .then(() => UserPreference.update(userId, pref));

    preferenceSaveQueue = request;

    await request;

    if (this.user.id === userId) {
      this.MERGE_PREFERENCE(pref);
    }
  }

  @Action({ rawError: true })
  public syncActingUnit(route: Route): void {
    const req = parseActingUnitParam(route);

    if (!req) {
      this.exitActingUnit();
      return;
    }

    const loginId = this.loginUnitId;

    if (this.unitSegment && this.unitSegment.unitId === req.unitId) {
      return;
    }

    if (this.actingApplied) this.RESTORE_LOGIN_UNIT();

    this.SET_UNIT_SEGMENT({
      unitId: req.unitId,
      unitNama: req.unitId,
      unitGroupId: null,
      zona: null,
      register: req.register
    });

    if (req.unitId === loginId || !this.canActAcrossUnit) return;

    if (!this.loginUnit) {
      this.SET_LOGIN_UNIT({
        unit_id: this.user?.unit_id ?? "",
        unit: this.user?.unit ?? "",
        unit_group_id: this.user?.unit_group_id ?? "",
        zona: this.user?.zona ?? ""
      });
    }

    this.APPLY_SEGMENT_TO_USER();
  }

  @Action({ rawError: true })
  public applyActingUnitMeta(meta: {
    unitId: string;
    unitNama: string;
    unitGroupId: string | null;
    zona: string | null;
  }): void {
    this.PATCH_SEGMENT_META(meta);
  }

  @Action({ rawError: true })
  public exitActingUnit(): void {
    if (!this.unitSegment && !this.actingApplied) return;

    if (this.actingApplied) this.RESTORE_LOGIN_UNIT();
    this.CLEAR_ACTING_STATE();
  }

  @Action({ rawError: true })
  public changeSessionUnit(unit: {
    kode: string;
    nama: string;
    unit_group_id: string;
    zona: string;
  }): void {
    this.exitActingUnit();

    const newSession = this.user;

    newSession.unit_id = unit.kode;
    newSession.unit = unit.nama;
    newSession.unit_group_id = unit.unit_group_id;
    newSession.zona = unit.zona;

    this.SET_USER(newSession);
    setUser(JSON.stringify(newSession));
  }

  @Action({ rawError: true })
  public async getProfile() {
    return await UserSvc.getMe().then(dt => {
      this.SET_PROFILE(dt.data);
    });
  }

  @Action({ rawError: true })
  public async resetSession(softReset = false) {
    this.SET_SESSION({
      msg: null,
      is_password_expired: false,
      is_password_reseted: false
    });

    const usr = getUser();
    usr.is_password_expired = false;
    usr.is_password_reseted = false;
    usr.sessionMessage = null;

    if (softReset) {
      this.SET_USER(usr);
    } else {
      setUser(JSON.stringify(usr));
    }
  }
}

export const userModule = getModule(UserModule);
